/**
 * Arena aggregate repository (ADR-031 P4).
 * Collection: `arenas/{arn_*}`.
 * CMOE keeps `competitions/{rs_*}` via cmoe repositories — never product Arena.
 */

import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocFromServer,
  getDocs,
  getDocsFromServer,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  limit,
  runTransaction,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { themeUrlsForFirestore } from "../domain/competition/themePhotoUrl";
import { getLocalEntityThemePhoto } from "./localEntityThemePhotoStorage";

const COLLECTION = "arenas";

function isBusinessArenaId(id) {
  const s = String(id || "");
  return s.startsWith("arn_") || s.startsWith("cmp_");
}

function arenaRef(arenaId) {
  return doc(db, COLLECTION, arenaId);
}

function normalizeArenaRow(d) {
  const data = d.data ? d.data() : d;
  const id = d.id || data.arenaId || data.competitionId;
  return withLocalThemePhoto({
    id,
    ...data,
    arenaId: data.arenaId || id,
    competitionId: data.competitionId || data.arenaId || id,
    liveSessionId: data.liveSessionId || data.roomId || null,
    roomId: data.liveSessionId || data.roomId || null,
  });
}

/** Device-local cover when the arena has no cloud URL (media API not configured). */
function withLocalThemePhoto(row) {
  const banner = row.bannerImageUrl;
  const logo = row.logoUrl;
  const hasCloud =
    (typeof banner === "string" && banner.startsWith("http") && banner.length <= 2048) ||
    (typeof logo === "string" && logo.startsWith("http") && logo.length <= 2048);
  if (hasCloud) return row;
  const local = getLocalEntityThemePhoto(
    "competition",
    row.arenaId || row.competitionId || row.id
  );
  if (!local) return row;
  return { ...row, bannerImageUrl: local };
}

/**
 * Fetch Arena with Event competitive hydrate (implementation detail).
 * @param {string} arenaId
 * @param {{ fromServer?: boolean }} [opts] Prefer server read after mutations (avoids stale IndexedDB cache).
 */
export async function fetchArena(arenaId, opts = {}) {
  if (!arenaId) return null;
  const ref = arenaRef(arenaId);
  let snap;
  try {
    snap = opts.fromServer ? await getDocFromServer(ref) : await getDoc(ref);
  } catch (err) {
    if (!opts.fromServer) throw err;
    snap = await getDoc(ref);
  }
  if (!snap.exists()) return null;
  const row = normalizeArenaRow(snap);
  if (opts.skipHydrate) return row;
  try {
    const { hydrateCompetitiveState } = await import("../services/tournamentEventState");
    return await hydrateCompetitiveState(row, opts);
  } catch {
    return row;
  }
}

/**
 * @param {string} arenaId
 * @param {Record<string, unknown>} data
 */
export async function createArenaDoc(arenaId, data) {
  const stored = themeUrlsForFirestore(data) || data;
  await setDoc(arenaRef(arenaId), {
    ...stored,
    arenaId: stored.arenaId || arenaId,
    competitionId: stored.competitionId || arenaId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * @param {string} arenaId
 * @param {Record<string, unknown>} data
 */
export async function updateArena(arenaId, data) {
  const stored = themeUrlsForFirestore(data) || data;
  await updateDoc(arenaRef(arenaId), {
    ...stored,
    updatedAt: serverTimestamp(),
  });
}

/**
 * @param {string} arenaId
 * @param {Record<string, unknown>} data
 */
export async function setArena(arenaId, data) {
  const stored = themeUrlsForFirestore(data) || data;
  await setDoc(
    arenaRef(arenaId),
    {
      ...stored,
      arenaId: stored.arenaId || arenaId,
      competitionId: stored.competitionId || arenaId,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

/**
 * @param {{ max?: number }} [options]
 */
export async function listRecentArenas(options = {}) {
  const max = Math.min(options.max || 40, 80);
  const q = query(
    collection(db, COLLECTION),
    orderBy("updatedAtMs", "desc"),
    limit(max)
  );
  const read = options.fromServer ? getDocsFromServer : getDocs;
  let snap;
  try {
    snap = await read(q);
  } catch (err) {
    if (!options.fromServer) throw err;
    snap = await getDocs(q);
  }
  return snap.docs
    .map((d) => normalizeArenaRow(d))
    .filter((row) => isBusinessArenaId(row.competitionId || row.arenaId || row.id));
}

/**
 * @param {string} arenaId
 * @param {Record<string, unknown>} [extra]
 */
export async function softArchiveArena(arenaId, extra = {}) {
  await updateDoc(arenaRef(arenaId), {
    status: "archived",
    updatedAtMs: Date.now(),
    archivedAtMs: Date.now(),
    ...extra,
    updatedAt: serverTimestamp(),
  });
}

/**
 * @param {string} arenaId
 * @param {Record<string, unknown>} [extra]
 */
export async function softDeleteArena(arenaId, extra = {}) {
  await updateDoc(arenaRef(arenaId), {
    status: "deleted",
    updatedAtMs: Date.now(),
    deletedAtMs: Date.now(),
    ...extra,
    updatedAt: serverTimestamp(),
  });
}

/**
 * @param {string} arenaId
 */
export async function deleteArena(arenaId) {
  await deleteDoc(arenaRef(arenaId));
}

/**
 * @param {string} uid
 * @param {{ max?: number, limitCount?: number }} [options]
 */
export async function listArenasForOwner(uid, options = {}) {
  if (!uid) return [];
  const max = Math.min(options.max || options.limitCount || 50, 100);
  const q = query(
    collection(db, COLLECTION),
    where("ownerIds", "array-contains", uid),
    orderBy("updatedAtMs", "desc"),
    limit(max)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => normalizeArenaRow(d))
    .filter((row) => isBusinessArenaId(row.competitionId || row.arenaId || row.id));
}

/**
 * @param {string} organizationId
 * @param {{ max?: number }} [options]
 */
export async function listArenasByOrganization(organizationId, options = {}) {
  if (!organizationId) return [];
  const max = Math.min(options.max || 40, 60);
  try {
    const q = query(
      collection(db, COLLECTION),
      where("organizationId", "==", organizationId),
      orderBy("updatedAtMs", "desc"),
      limit(max)
    );
    const snap = await getDocs(q);
    return snap.docs
      .map((d) => normalizeArenaRow(d))
      .filter((row) => isBusinessArenaId(row.competitionId || row.arenaId || row.id));
  } catch {
    const recent = await listRecentArenas({ max: Math.min(max * 2, 80) });
    return recent.filter((c) => c.organizationId === organizationId).slice(0, max);
  }
}

/**
 * @param {string} arenaId
 * @param {(current: object) => { next: object, error?: string }} mutator
 */
export async function runArenaTransaction(arenaId, mutator) {
  const ref = arenaRef(arenaId);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      throw new Error("Arena not found.");
    }
    const current = normalizeArenaRow(snap);
    const result = mutator(current);
    if (result.error) {
      throw new Error(result.error);
    }
    tx.update(ref, {
      ...result.next,
      updatedAt: serverTimestamp(),
    });
    return { ...current, ...result.next };
  });
}

/**
 * Subscribe to Arena + Event competitive hydrate.
 * @param {string} arenaId
 * @param {(data: object | null) => void} onData
 */
export function subscribeArena(arenaId, onData) {
  if (!arenaId) {
    onData(null);
    return () => {};
  }

  let cancelled = false;
  let unsubEvents = () => {};
  let arenaRow = null;
  let gen = 0;

  const emitHydrated = async () => {
    const myGen = ++gen;
    if (!arenaRow) {
      onData(null);
      return;
    }
    try {
      const { hydrateCompetitiveState } = await import("../services/tournamentEventState");
      const hydrated = await hydrateCompetitiveState(arenaRow);
      if (!cancelled && myGen === gen) onData(hydrated);
    } catch {
      if (!cancelled && myGen === gen) onData(arenaRow);
    }
  };

  const unsubArena = onSnapshot(arenaRef(arenaId), (snap) => {
    if (!snap.exists()) {
      arenaRow = null;
      unsubEvents();
      unsubEvents = () => {};
      onData(null);
      return;
    }
    arenaRow = normalizeArenaRow(snap);
    unsubEvents();
    import("../repositories/tournamentEventRepository")
      .then((repo) => {
        if (cancelled) return;
        unsubEvents = repo.subscribeEvents(arenaId, () => {
          void emitHydrated();
        });
      })
      .catch(() => {
        void emitHydrated();
      });
    void emitHydrated();
  });

  return () => {
    cancelled = true;
    unsubArena();
    unsubEvents();
  };
}

export { COLLECTION as ARENAS_COLLECTION };

/** @deprecated Prefer ARENAS_COLLECTION */
export const COMPETITIONS_COLLECTION = COLLECTION;

/** @deprecated Prefer Arena-named exports (ADR-031 P4) */
export const fetchCompetition = fetchArena;
/** @deprecated */
export const createCompetition = createArenaDoc;
/** @deprecated */
export const updateCompetition = updateArena;
/** @deprecated */
export const setCompetition = setArena;
/** @deprecated */
export const deleteCompetition = deleteArena;
/** @deprecated */
export const subscribeCompetition = subscribeArena;
/** @deprecated */
export const listCompetitionsForUser = listArenasForOwner;
/** @deprecated */
export const listCompetitionsForOwner = listArenasForOwner;
/** @deprecated */
export const listRecentCompetitions = listRecentArenas;
/** @deprecated */
export const listCompetitionsByOrganization = listArenasByOrganization;
/** @deprecated */
export const softArchiveCompetition = softArchiveArena;
/** @deprecated */
export const softDeleteCompetition = softDeleteArena;
/** @deprecated */
export const runCompetitionTransaction = runArenaTransaction;
